import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useDeliveryManagement } from "../useHook";
import { MdDelete } from "react-icons/md";
import Loader from "../../../Utils/loader";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import OutStationRatesModal from "./component/OutStationRatesModal";
import { FiEdit2, FiPlus } from "react-icons/fi";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const OutStationBaseRatesPage = () => {
  const {
    getOutStationBaseRates,
    addOutStationBaseRate,
    updateOutStationBaseRate,
    deleteOutStationBaseRate,
    getAllOutStationZones,
  } = useDeliveryManagement();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [outStationData, setOutStationData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [zones, setZones] = useState([]);

  const { hasPermission } = usePermission();
  const canCreate = hasPermission(SystemPermissions.OutStationBaseRate.CREATE);
  const canUpdate = hasPermission(SystemPermissions.OutStationBaseRate.UPDATE);
  const canDelete = hasPermission(SystemPermissions.OutStationBaseRate.DELETE);
  const showActions = canUpdate || canDelete;

  const [pagination, setPagination] = useState({
    totalRecords: 0,
    totalPages: 0,
    currentPage: 1,
    perPage: 10,
  });
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
  };

  const fetchOutStationData = async () => {
    try {
      setIsLoading(true);
      const response = await getOutStationBaseRates(
        setOutStationData,
        offset,
        limit,
      );

      if (response?.pagination) {
        setPagination({
          totalRecords: response.pagination.totalRecords,
          totalPages: response.pagination.totalPages,
          currentPage: response.pagination.currentPage,
          perPage: response.pagination.perPage,
        });
      }
    } catch (error) {
      console.error("Error fetching out-station data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOutStationData();
    getAllOutStationZones(setZones);
  }, [offset]);

  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
  };

  const handleAddOrUpdate = async (formData) => {
    try {
      if (editData) {
        const response = await updateOutStationBaseRate(editData.id, formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Out-Station Updated",
            text: "Your out-station configuration has been successfully updated!",
            confirmButtonText: "OK",
          });
        }
      } else {
        const response = await addOutStationBaseRate(formData);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Out-Station Added",
            text: "Your out-station configuration has been successfully added!",
            confirmButtonText: "OK",
          });
        }
      }
      await fetchOutStationData();
    } catch (error) {
      console.log(error);
      Swal.fire({
        icon: "error",
        title: "Failed to Save Out-Station",
        text: error?.response?.data?.message,
        confirmButtonText: "OK",
      });
    }
    closeModal();
  };

  const handleEdit = (data) => {
    setEditData(data);
    openModal();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteOutStationBaseRate(id);
        if (response) {
          Swal.fire({
            icon: "success",
            title: "Deleted!",
            text: "The out-station configuration has been deleted.",
            confirmButtonText: "OK",
          });
          await fetchOutStationData();
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Failed to Delete",
          text: "An error occurred while deleting the out-station configuration. Please try again.",
          confirmButtonText: "OK",
        });
      }
    }
  };

  if (isLoading) {
    return <Loader message="Loading out-station data..." />;
  }

  return (
    <div>
      <div>
        <div className="flex flex-wrap gap-4 justify-between items-center">
          <h2 className="text-2xl font-bold">Manage Out-Station Base Rates</h2>
          {canCreate && (
            <button onClick={openModal} className="btn btn-primary h-[44px]">
              <FiPlus className="text-white" />
              Add Out-Station Base Rate
            </button>
          )}
        </div>

        <div className="bg-white w-full rounded-lg pagination-shadow my-5">
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto bg-white rounded-lg overflow-hidden">
              <thead>
                <tr className="brand-gradient text-white">
                  <th className="table-th">Sr. No</th>
                  <th className="table-th">Zone Name</th>
                  <th className="table-th">Min Weight</th>
                  <th className="table-th">Max Weight</th>
                  <th className="table-th">Builty Rate</th>
                  <th className="table-th">Coach Rate</th>
                  <th className="table-th">Adda Delivery</th>
                  {showActions && <th className="table-th">Action</th>}
                </tr>
              </thead>
              <tbody>
                {outStationData && outStationData.length > 0 ? (
                  outStationData.map((item, index) => (
                    <tr
                      key={item.id}
                      className={`${
                        index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                      } hover:bg-gray-200 transition-colors duration-300`}
                    >
                      <td className="table-td">{offset + index + 1}</td>
                      <td className="table-td">
                        {item.zone?.zone_name || "N/A"}
                      </td>
                      <td className="table-td">{item.min_weight}</td>
                      <td className="table-td">{item.max_weight}</td>
                      <td className="table-td">{item.builty_rate}</td>
                      <td className="table-td">{item.coach_rate}</td>
                      <td className="table-td">{item.adda_delivery}</td>
                      {showActions && (
                        <td className="table-td flex space-x-2">
                          {canUpdate && (
                            <button
                              className="btn btn-edit text-white"
                              onClick={() => handleEdit(item)}
                            >
                              <FiEdit2 className="text-white" /> Edit
                            </button>
                          )}
                          {canDelete && (
                            <button
                              className="btn btn-delete text-white"
                              onClick={() => handleDelete(item.id)}
                            >
                              <MdDelete className="text-white" /> Delete
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={showActions ? 8 : 7} className="text-center py-4">
                      No out-station base rates found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {/* Pagination Component */}
            {pagination.totalPages > 1 && (
              <div className="mt-6">
                <GlobalPagination
                  offset={offset}
                  limit={limit}
                  total={pagination.totalRecords}
                  onPageChange={handlePageChange}
                  className="mt-4"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <OutStationRatesModal
        isOpen={isModalOpen}
        onClose={closeModal}
        onSubmit={handleAddOrUpdate}
        editData={editData}
        zones={zones}
      />
    </div>
  );
};

export default OutStationBaseRatesPage;
